import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Alert, Card, CardContent } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useDispatch, useSelector } from "react-redux";
import TriggerBaseForm from "./UI/reusable-components/ProductSyncComponents/TriggerBaseForm";
import TriggerTargetList from "./UI/reusable-components/ProductSyncComponents/TriggerTargetList";
import TriggerLogTable from "./UI/reusable-components/ProductSyncComponents/TriggerLogTable";
import {
  GetPlaceProductTrigger,
  GetPlaceOptionsList,
} from "../redux/actions/productSyncActions";

const ProductSync = () => {
  const authUserId = useSelector((state) => state?.auth?.user?.id);
  const dispatch = useDispatch();

  const [trigger, setTrigger] = useState(null);
  const [placeOptions, setPlaceOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [triggerList, options] = await Promise.all([
        dispatch(GetPlaceProductTrigger(authUserId)),
        dispatch(GetPlaceOptionsList()),
      ]);
      setPlaceOptions(options);
      setTrigger(triggerList && triggerList.length > 0 ? triggerList[0] : null);
    } catch (error) {
      console.error("Senkronizasyon verileri alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authUserId) fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserId]);

  const baseKey = trigger?.baseMarketPlaceId
    ? `mp-${trigger.baseMarketPlaceId}`
    : trigger?.baseEcommercePlaceId
    ? `ec-${trigger.baseEcommercePlaceId}`
    : "";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Yayılım
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Bir kaynak platformdan aldığınız ürünleri, tanımladığınız fiyat kurallarıyla birlikte
        diğer pazaryeri/e-ticaret platformlarına otomatik olarak aktarın.
      </Typography>

      <Card
        sx={{
          mb: 3,
          backgroundColor: "#E3F2FD",
          borderLeft: "4px solid #2196F3",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <InfoIcon sx={{ mr: 1, color: "#2196F3" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Nasıl Çalışır?
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ ml: 4, color: "#424242" }}>
            Yayılım, sipariş beklemeden çalışan, zaman tetiklemeli (periyodik) bir fiyat ve
            stok dağıtım sistemidir:
            <br />
            <br />
            • <strong>1. Adım:</strong> Ürünlerin temel alınacağı bir kaynak (base)
            pazaryeri/e-ticaret platformu seçilir.
            <br />
            • <strong>2. Adım:</strong> Bu ürünlerin aktarılacağı hedef pazaryerleri eklenir;
            isteğe bağlı olarak her hedef için ayrı bir fiyat kuralı (yüzde veya sabit tutar,
            ekleme ya da çıkarma) tanımlanabilir. Fiyat kuralı <strong>zorunlu değildir</strong>
            — bir hedefte fiyatın kaynak pazaryeriyle (ör. Trendyol) birebir aynı kalmasını
            istiyorsanız, o hedef için Miktar alanına 0 girebilir ya da kuralı hiç
            kaydetmeyebilirsiniz.
            <br />
            • <strong>3. Adım:</strong> Sistem, kaynak platformdaki fiyat ve stok bilgilerini
            yaklaşık her 10 dakikada bir kontrol eder; bir değişiklik tespit ederse tanımlı
            kurallara göre hesaplanan yeni değerleri (stok dahil) otomatik olarak hedef
            platformlara yansıtır.
            <br />
            <br />
            Bu akış, kaynak platformda sipariş oluşup oluşmadığından tamamen bağımsızdır —
            yalnızca kaynaktaki fiyat/stok değişikliğini izler. Sipariş bazlı çalışan{" "}
            <strong>Stok Takibi</strong> özelliğinden farklıdır; ikisi birbirinden bağımsız
            çalışır ve aynı anda kullanılabilir.
          </Typography>
        </CardContent>
      </Card>

      <TriggerBaseForm placeOptions={placeOptions} trigger={trigger} onSaved={setTrigger} />

      {!trigger && (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          Hedef pazaryerleri ve fiyat kurallarını tanımlayabilmek için önce yukarıdan bir kaynak
          platform seçip kaydedin.
        </Alert>
      )}

      {trigger && (
        <>
          <TriggerTargetList triggerId={trigger.id} placeOptions={placeOptions} baseKey={baseKey} />
          <TriggerLogTable triggerId={trigger.id} />
        </>
      )}
    </Box>
  );
};

export default ProductSync;
